/**
 * Product Form — PDP variant selection, thumbnail gallery, add-to-cart
 *
 * Depends on:
 *   - A <script id="product-json" type="application/json"> block in the
 *     product template containing the Shopify product JSON.
 *   - window.openCart() and window.refreshCartDrawer() from cart-drawer.js
 *
 * DOM expectations (BEM-ish classes from the colibri theme):
 *   .pdp-hero          — outer product container
 *   .pdp-main-image    — main product image wrapper
 *   .pdp-thumb         — thumbnail items
 *   .pdp-sizes         — variant selector section
 *   .pdp-size-option   — individual variant option (wraps a radio input)
 *   .pdp-price         — current price display
 *   .pdp-compare-price — compare-at (strike-through) price
 *   .pdp-unit-price    — unit price (Grundpreis) display
 *   .pdp-atc-btn       — add-to-cart button
 *   #variant-id        — hidden input for selected variant ID
 *   #product-json      — JSON script block with product data
 */
(function() {
  'use strict';

  /* ── Guard: only run on product pages ── */
  var container = document.querySelector('.pdp-hero');
  if (!container) return;

  /* ── Parse product JSON ── */
  var productJsonEl = document.getElementById('product-json');
  if (!productJsonEl) return;

  var product;
  try {
    product = JSON.parse(productJsonEl.textContent);
  } catch (e) {
    console.error('[product-form] Failed to parse product JSON:', e);
    return;
  }

  var variants = product.variants || [];

  /* ── Helpers ── */

  /**
   * Format a price number (in subunit / cents) to a display string.
   * Uses shop.money_format from window.__shopMoney for currency-aware output.
   */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    if (window.__shopMoney && window.__shopMoney.format) {
      return window.__shopMoney.format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
        .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
    }
    return amount + ' \u20AC';
  }

  /**
   * Escape a string for safe use inside HTML attribute values.
   */
  function escAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /**
   * Append/replace a width parameter on a Shopify CDN image URL.
   */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    if (/[?&]width=/.test(src)) {
      return src.replace(/([?&])width=\d+/, '$1width=' + width);
    }
    var sep = src.indexOf('?') === -1 ? '?' : '&';
    return src + sep + 'width=' + width;
  }

  /**
   * Find a variant object by its numeric ID.
   */
  function findVariant(id) {
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === id) return variants[i];
    }
    return null;
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Variant Selection                                                 */
  /* ────────────────────────────────────────────────────────────────── */

  var sizeInputs = container.querySelectorAll('input[name="variant-select"]');

  /* Ensure clicking the label properly selects the radio even when visually hidden */
  container.querySelectorAll('.pdp-size-option').forEach(function(label) {
    label.addEventListener('click', function(e) {
      var radio = this.querySelector('input[type="radio"]');
      if (radio && !radio.disabled && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  sizeInputs.forEach(function(input) {
    input.addEventListener('change', function() {
      var variantId = parseInt(this.value, 10);
      var variant = findVariant(variantId);
      if (!variant) return;

      /* Update URL without reload */
      var url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      history.replaceState({}, '', url);

      /* Update hidden form input */
      var hiddenInput = document.getElementById('variant-id');
      if (hiddenInput) hiddenInput.value = variantId;

      /* ── Price display ── */
      var priceEl       = container.querySelector('[data-product-price]');
      var unitPriceEl   = container.querySelector('[data-unit-price]');

      if (priceEl) {
        var priceFormatted = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          var pdpPct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          priceEl.innerHTML = '<span class="pdp-price--sale">' + priceFormatted + '</span>' +
            '<s class="pdp-price--compare">' + formatMoney(variant.compare_at_price) + '</s>' +
            '<span class="pdp-price-badge">-' + pdpPct + '%</span>';
        } else {
          priceEl.textContent = priceFormatted;
        }
      }

      /* Unit price (Grundpreis — German PAngV requirement) */
      if (unitPriceEl) {
        if (variant.unit_price) {
          var refValue = variant.unit_price_measurement ? variant.unit_price_measurement.reference_value : '';
          var refUnit = variant.unit_price_measurement ? variant.unit_price_measurement.reference_unit : '';
          unitPriceEl.textContent = '(' + formatMoney(variant.unit_price) + '/' + refValue + refUnit + ')';
          unitPriceEl.style.display = '';
        } else {
          unitPriceEl.style.display = 'none';
        }
      }

      /* ── Main image ── */
      var variantImage = variant.featured_image;
      /* Fallback: if variant has no dedicated image, use the product's featured image */
      if (!variantImage && product.featured_image) {
        variantImage = typeof product.featured_image === 'string'
          ? { src: product.featured_image, alt: product.title }
          : product.featured_image;
      }
      if (variantImage && variantImage.src) {
        var targetMediaId = variantImage.id || (variant.featured_image && variant.featured_image.id);
        var slidesWrap = container.querySelector('[data-pdp-slides]');
        var targetSlide = targetMediaId && slidesWrap
          ? slidesWrap.querySelector('.pdp-slide[data-media-id="' + targetMediaId + '"]')
          : null;

        if (targetSlide && slidesWrap) {
          /* Slide ist bereits im DOM (häufig — Variant-Image ist meist Teil
             von product.media). Active-Klasse umsetzen + zum Slide scrollen.
             Desktop: CSS zeigt nur .active-Slide (display:block); Scrolling ist
             no-op weil pdp-main-slides dort kein Scroll-Container ist.
             Mobile: scroll-snap snapt visuell zum neuen Slide. */
          slidesWrap.querySelectorAll('.pdp-slide').forEach(function(s){
            s.classList.remove('active');
          });
          targetSlide.classList.add('active');
          /* scrollLeft direkt setzen (anstelle von scrollIntoView) — scrollIntoView
             würde auch das Page-Scrolling triggern. */
          slidesWrap.scrollLeft = targetSlide.offsetLeft;
        } else {
          /* Edge-Case: Variant-Image ist NICHT Teil von product.media. Fallback
             auf Single-Image-Replace (überschreibt das Slides-Setup für diese
             Session). Safari-Bug: ganzes <img>-Node ersetzen, nicht nur .src. */
          var newSrc = getSizedImageUrl(variantImage.src, 1200);
          var mainMediaWrap = container.querySelector('#pdp-main-media');
          if (mainMediaWrap) {
            var imgW = variantImage.width || '';
            var imgH = variantImage.height || '';
            mainMediaWrap.innerHTML =
              '<img id="pdp-main-img" src="' + newSrc + '"' +
              ' alt="' + escAttr(variantImage.alt || product.title) + '"' +
              (imgW ? ' width="' + imgW + '"' : '') +
              (imgH ? ' height="' + imgH + '"' : '') + '>';
          }
        }

        /* Activate the matching thumbnail */
        if (targetMediaId) {
          container.querySelectorAll('.pdp-thumb').forEach(function(t) {
            t.classList.remove('active');
            if (t.getAttribute('data-media-id') == targetMediaId) {
              t.classList.add('active');
            }
          });
        }
      }

      /* ── ATC button state ── */
      var atcBtn = container.querySelector('.pdp-add-to-cart');
      if (atcBtn) {
        var atcText = atcBtn.querySelector('[data-add-to-cart-text]');
        if (variant.available) {
          atcBtn.disabled = false;
          if (atcText) atcText.textContent = 'In den Warenkorb';
        } else {
          atcBtn.disabled = true;
          if (atcText) atcText.textContent = 'Ausverkauft';
        }
      }

      /* Sold-out-Status am Container togglen → CSS blendet Warenkorb-Button und
         Sticky-Bar aus; bei verfügbarer Variante blendet CSS umgekehrt einen
         ggf. schon injizierten Klaviyo-BIS-Button aus (siehe base.css).
         Kein Warten auf Klaviyo: sold-out geladene Seiten verstecken den
         grauen „Ausverkauft"-Button von Anfang an — blockt der Browser Klaviyo
         (Firefox ETP, Edge, Adblocker), bleibt bewusst nur der Hinweis
         „Aktuell nicht verfügbar" (Entscheidung 2026-07).
         Guard via #OOS/BIS-Button: wurde die Seite mit VERFÜGBARER Variante
         geladen, gibt es keinen #OOS-Anker und Klaviyo injiziert nie — beim
         Wechsel auf eine ausverkaufte Variante bleibt dann der deaktivierte
         graue Button als Status stehen (kein Verstecken ins Leere).
         Klaviyo ignoriert die konfigurierte product_page_class — der echte
         Button trägt nur generierte kl-private-reset-css-Klassen. */
      var hasBis = !!container.querySelector('#OOS') ||
        !!container.querySelector('.klaviyo-bis-button, .pdp-form button[class*="kl-private-reset-css"]');
      container.classList.toggle('pdp-hero--sold-out', !variant.available && hasBis);

      /* Express-Checkout-Buttons: nur bei kaufbarer Variante zeigen — Shopify
         würde sie bei sold-out sonst ausgegraut rendern. */
      var expressEl = container.querySelector('[data-pdp-express]');
      if (expressEl) expressEl.hidden = !variant.available;

      /* ── Delivery estimate update ── */
      if (window.DeliveryEstimate) {
        window.DeliveryEstimate.update(variant.available);
      }

      /* ── Größen-/Reichweite-Hinweis: nur bei der größten Variante anzeigen ── */
      var sizeHint = container.querySelector('[data-size-hint]');
      if (sizeHint && !sizeHint.hasAttribute('data-single')) {
        var largestVariantId = sizeHint.getAttribute('data-largest-variant');
        sizeHint.classList.toggle('is-hidden', String(variantId) !== String(largestVariantId));
      }

      /* ── Active class on size options ── */
      container.querySelectorAll('.pdp-size-option').forEach(function(label) {
        label.classList.remove('active');
      });
      var parentOption = this.closest('.pdp-size-option');
      if (parentOption) parentOption.classList.add('active');
    });
  });

  /* ────────────────────────────────────────────────────────────────── */
  /*  Legacy selectSize() support (onclick inline handlers)             */
  /* ────────────────────────────────────────────────────────────────── */

  /**
   * The prototype HTML uses onclick="selectSize(this, price, unit)".
   * We expose a global to keep those working until templates are migrated
   * to radio-input based selection.
   */
  window.selectSize = function(el, price, unit) {
    container.querySelectorAll('.pdp-size-option').forEach(function(o) {
      o.classList.remove('active');
    });
    el.classList.add('active');

    var priceEl = container.querySelector('.pdp-price');
    var unitEl  = container.querySelector('.pdp-price-unit') || container.querySelector('.pdp-unit-price');
    if (priceEl) priceEl.textContent = price;
    if (unitEl)  unitEl.textContent  = unit;
  };

  /* ────────────────────────────────────────────────────────────────── */
  /*  Thumbnail Gallery                                                 */
  /* ────────────────────────────────────────────────────────────────── */

  var mainMediaWrap = container.querySelector('#pdp-main-media');

  container.querySelectorAll('.pdp-thumb').forEach(function(thumb) {
    thumb.addEventListener('click', function() {
      var mediaType = this.getAttribute('data-media-type');

      /* Update active state */
      container.querySelectorAll('.pdp-thumb').forEach(function(t) {
        t.classList.remove('active');
      });
      this.classList.add('active');

      if (mediaType === 'video') {
        /* Shopify-hosted video */
        var sources = [];
        try { sources = JSON.parse(this.getAttribute('data-video-sources')); } catch(e) {}
        var alt = this.getAttribute('data-video-alt') || '';
        if (sources.length && mainMediaWrap) {
          var sourceHtml = sources.map(function(s) {
            return '<source src="' + s.url + '" type="' + s.mime + '">';
          }).join('');
          mainMediaWrap.innerHTML =
            '<video class="pdp-main-video" controls playsinline autoplay muted aria-label="' + escAttr(alt) + '">' +
            sourceHtml + '</video>';
        }

      } else if (mediaType === 'external_video') {
        /* YouTube / Vimeo */
        var host = this.getAttribute('data-host');
        var extId = this.getAttribute('data-external-id');
        if (mainMediaWrap && extId) {
          var iframeSrc = '';
          if (host === 'youtube') {
            iframeSrc = 'https://www.youtube.com/embed/' + extId + '?autoplay=1';
          } else if (host === 'vimeo') {
            iframeSrc = 'https://player.vimeo.com/video/' + extId + '?autoplay=1';
          }
          if (iframeSrc) {
            mainMediaWrap.innerHTML =
              '<iframe class="pdp-main-video" src="' + iframeSrc + '" ' +
              'frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>';
          }
        }

      } else {
        /* Image (default): bevorzugt das vorgerenderte Slide aktivieren — dann
           bleibt das Swipe-Setup auf Mobile intakt. Fallback auf innerHTML-
           Replace nur wenn kein matching Slide existiert. */
        var mediaId = this.getAttribute('data-media-id');
        var slidesWrap = container.querySelector('[data-pdp-slides]');
        var targetSlide = mediaId && slidesWrap
          ? slidesWrap.querySelector('.pdp-slide[data-media-id="' + mediaId + '"]')
          : null;
        if (targetSlide && slidesWrap) {
          slidesWrap.querySelectorAll('.pdp-slide').forEach(function(s){
            s.classList.remove('active');
          });
          targetSlide.classList.add('active');
          slidesWrap.scrollLeft = targetSlide.offsetLeft;
        } else if (mainMediaWrap) {
          var imgSrc = this.getAttribute('data-img-src');
          var thumbImg = this.querySelector('img');
          if (!imgSrc && thumbImg) {
            imgSrc = thumbImg.src.replace(/width=\d+/, 'width=1200');
          }
          mainMediaWrap.innerHTML =
            '<img id="pdp-main-img" src="' + imgSrc + '" alt="' + escAttr(thumbImg ? thumbImg.alt : '') + '">';
        }
      }
    });
  });

  /* ────────────────────────────────────────────────────────────────── */
  /*  Mobile-Swipe-Sync: aktive Thumbnail dem mittigen Slide folgen     */
  /* ────────────────────────────────────────────────────────────────── */

  /* Sobald der User mit dem Daumen wischt (oder per Drag scrollt), markieren
     wir die Thumbnail des aktuell mittigen Slides als aktiv. Throttle via
     requestAnimationFrame; matchMedia stellt sicher, dass der Listener
     auf Desktop kein Overhead verursacht. */
  var slidesWrap = container.querySelector('[data-pdp-slides]');
  if (slidesWrap) {
    var mobileMql = window.matchMedia('(max-width: 900px)');
    var rafId = 0;
    function syncActiveFromScroll() {
      var slides = slidesWrap.querySelectorAll('.pdp-slide');
      if (!slides.length) return;
      var wrapRect = slidesWrap.getBoundingClientRect();
      var centerX = wrapRect.left + wrapRect.width / 2;
      var bestIdx = 0;
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var sr = slides[i].getBoundingClientRect();
        var sMid = sr.left + sr.width / 2;
        var d = Math.abs(sMid - centerX);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
      var activeMediaId = slides[bestIdx].getAttribute('data-media-id');
      for (var j = 0; j < slides.length; j++) {
        if (j === bestIdx) slides[j].classList.add('active');
        else slides[j].classList.remove('active');
      }
      if (activeMediaId) {
        container.querySelectorAll('.pdp-thumb').forEach(function(t){
          if (t.getAttribute('data-media-id') === activeMediaId) t.classList.add('active');
          else t.classList.remove('active');
        });
      }
    }
    function onSlidesScroll() {
      if (!mobileMql.matches) return;
      if (rafId) return;
      rafId = window.requestAnimationFrame(function(){
        rafId = 0;
        syncActiveFromScroll();
      });
    }
    slidesWrap.addEventListener('scroll', onSlidesScroll, { passive: true });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Add to Cart — form interception                                   */
  /* ────────────────────────────────────────────────────────────────── */

  var form = container.querySelector('form[action="/cart/add"]');
  if (form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();

      /* e.submitter = der tatsächlich geklickte Button (Haupt-CTA ODER Sticky-Bar-
         Button via form-Attribut). Fallback für ältere Safari (< 15.4). */
      var btn = e.submitter || form.querySelector('button[type="submit"]');
      if (!btn) btn = form.querySelector('.pdp-atc-btn') || form.querySelector('.pdp-add-to-cart');

      /* Loading state */
      var originalText = '';
      if (btn) {
        originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Wird hinzugef\u00FCgt\u2026';
      }

      var formData = new FormData(form);

      if (typeof window.addToCart === 'function') {
        /* Use the centralised addToCart from cart-drawer.js */
        window.addToCart(formData)
          .then(function() {
            resetButton(btn, originalText);
          })
          .catch(function() {
            showError(btn, originalText);
          });
      } else {
        /* Fallback: direct fetch if cart-drawer.js is not loaded */
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          body: formData
        })
        .then(function(r) {
          if (!r.ok) throw new Error('Add to cart failed');
          return r.json();
        })
        .then(function() {
          if (typeof window.refreshCartDrawer === 'function') window.refreshCartDrawer();
          if (typeof window.openCart === 'function') window.openCart();
          resetButton(btn, originalText);
        })
        .catch(function(err) {
          console.error('[product-form] addToCart failed:', err);
          showError(btn, originalText);
        });
      }
    });
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  Sticky Add-to-Cart-Bar — einblenden, sobald der Haupt-CTA aus dem   */
  /*  Viewport nach oben gescrollt ist.                                  */
  /* ────────────────────────────────────────────────────────────────── */

  var stickyBar = document.querySelector('[data-pdp-sticky]');
  var mainCta = container.querySelector('.pdp-add-to-cart');
  if (stickyBar && mainCta && 'IntersectionObserver' in window) {
    var stickyObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        /* Nur zeigen, wenn der CTA oberhalb des Viewports liegt (User ist
           daran vorbeigescrollt) — nicht, wenn er noch darunter wartet. */
        var aboveViewport = entry.boundingClientRect.top < 0;
        if (!entry.isIntersecting && aboveViewport) {
          stickyBar.classList.add('visible');
          stickyBar.setAttribute('aria-hidden', 'false');
        } else {
          stickyBar.classList.remove('visible');
          stickyBar.setAttribute('aria-hidden', 'true');
        }
      });
    }, { threshold: 0 });
    stickyObserver.observe(mainCta);
  }

  /**
   * Reset button back to normal state.
   */
  function resetButton(btn, text) {
    if (!btn) return;
    btn.disabled = false;
    btn.textContent = text || 'In den Warenkorb';
  }

  /**
   * Show brief error feedback then restore button.
   */
  function showError(btn, originalText) {
    if (!btn) return;
    btn.textContent = 'Fehler \u2013 bitte erneut versuchen';
    btn.disabled = false;
    setTimeout(function() {
      btn.textContent = originalText || 'In den Warenkorb';
    }, 2500);
  }

  /* ────────────────────────────────────────────────────────────────── */
  /*  URL-based variant pre-selection on page load                      */
  /* ────────────────────────────────────────────────────────────────── */

  (function preselectVariant() {
    var params = new URLSearchParams(window.location.search);
    var variantParam = params.get('variant');
    if (!variantParam) return;

    var input = container.querySelector('input[name="variant-select"][value="' + variantParam + '"]');
    if (input) {
      input.checked = true;
      /* Trigger change to update price/image/button */
      var evt = new Event('change', { bubbles: true });
      input.dispatchEvent(evt);
    }
  })();

})();
